import { RollbackOutlined } from '@ant-design/icons';
import { Button, Drawer, Tooltip, message } from 'antd';
import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { PaymentInfoTables } from 'components/PaymentsInfoTables';
import { FETCH_ERROR } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useFilters } from 'hooks/useFilters';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { useEffect, useMemo, useState } from 'react';
import { PaymentsService } from 'services/PaymentsService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { CardPaymentRefundForm } from './CardPaymentRefundForm';
import { CardPaymentsTable } from './CardPaymentsTable';

const taxTypes = new Set(['opv', 'ipn', 'vsms', 'osms', 'vosms', 'so']);
const refundablePaymentStatuses = new Set(['paid', 'success']);
const PAGE_TITLE = 'Карточные платежи';

function CardPayments() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [loading, setLoading] = useState(false);
  const [paymentRefundActive, setPaymentRefundActive] = useState(false);
  const [cardPayments, setCardPayments] = useState([]);
  const tribeId = useTribe();
  const [summary, setSummary] = useState({
    total_amount: 0,
    total_fee: 0,
    total_payda_fee: 0,
    total_visa_amount: 0,
  });
  const initialPaymentsInfo = {
    cardPayments: null,
    clientsPayments: null,
    bccPayments: null,
  };
  const [paymentsInfo, setPaymentsInfo] = useState(initialPaymentsInfo);
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: null,
    status: [],
    period: null,
    ordernum: null,
    f_created: null,
    t_created: null,
    send_date: null,
    f_sum: undefined,
    t_sum: undefined,
    visa: false,
    desc: null,
    pay_type: null,
  });
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const {
    state: { drawerVisible, activeRecord },
    openDrawer,
    closeDrawer,
  } = useDrawers();

  useEffect(() => {
    fetchCardPayments();
  }, [page, perPage, filters]);

  useEffect(() => {
    if (activeRecord) {
      fetchPaymentsInfo();
    }
  }, [activeRecord]);

  const fetchCardPayments = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const {
        items,
        total,
        total_amount,
        total_fee,
        total_payda_fee,
        total_visa_amount,
      } = await PaymentsService.getCardPayments(
        page,
        perPage,
        filters,
        tribeId
      );

      setCardPayments(items);
      setTotalRows(total);
      setCount(total);
      setSummary({
        total_amount,
        total_fee,
        total_payda_fee,
        total_visa_amount,
      });
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsInfo = async () => {
    setLoading(true);

    try {
      const { card_payment, payment, bcc } =
        await PaymentsService.getCardPaymentsDetail(activeRecord?.id);

      setPaymentsInfo({
        cardPayments: card_payment,
        clientsPayments: payment,
        bccPayments: bcc,
      });
    } catch (err) {
      setPaymentsInfo(initialPaymentsInfo);
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  const handleRefundSuccess = () => {
    closeDrawer();
    fetchCardPayments();
  };

  const canDoRefund = useMemo(() => {
    if (activeRecord && paymentsInfo.clientsPayments?.length) {
      const { clientsPayments } = paymentsInfo;
      const isTaxPayment = clientsPayments.some((payment) =>
        taxTypes.has(payment?.type)
      );
      const isRefundablePaymentStatus = refundablePaymentStatuses.has(
        activeRecord.status
      );

      return (
        !isTaxPayment &&
        isRefundablePaymentStatus &&
        (activeRecord?.type === 'acquiring' ||
          activeRecord?.type === 'card_token')
      );
    }
    return false;
  }, [activeRecord, paymentsInfo]);

  const renderExtraActions = () => {
    if (!canDoRefund) {
      return null;
    }

    return (
      <AccessControl pageId="cardPaymentReversal" action="create">
        <Tooltip placement="bottomLeft" title="Оформить возврат">
          <Button
            onClick={() => setPaymentRefundActive(true)}
            icon={<RollbackOutlined />}
          />
        </Tooltip>
      </AccessControl>
    );
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />
      </ActionsWrapper>

      <Drawer
        title={activeRecord?.company_name}
        width="70%"
        onClose={closeDrawer}
        visible={drawerVisible}
        extra={renderExtraActions()}
      >
        <PaymentInfoTables loading={loading} payments={paymentsInfo} />

        <CardPaymentRefundForm
          isActive={paymentRefundActive}
          initialValues={activeRecord}
          onClose={() => setPaymentRefundActive(false)}
          onSuccess={handleRefundSuccess}
        />
      </Drawer>

      <CardPaymentsTable
        allowFilter
        items={cardPayments}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        filters={filters}
        summary={summary}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={(value) => {
          handleFilters(value);
          resetPagination();
        }}
      />
    </>
  );
}

export { CardPayments };
